import { ViralWidgetDiscountValueType } from "@redotech/cockroachdb-codegen/tables/conversion/viral-widget-discount-value-type";
import type { Currency } from "@redotech/money/currencies";
import { getCurrencySymbol } from "@redotech/money/currencies";
import { assertNever } from "@redotech/util/type";
import * as z from "zod";

export const DEFAULT_VIRAL_WIDGET_TITLE =
  "Share this discount code with your friends!";

export const DEFAULT_VIRAL_WIDGET_MESSAGE =
  "Your friends get {{shareDiscount}} with this code. Each time someone uses it, we'll email you a code for {{rewardDiscount}} your next order.";

export const DEFAULT_VIRAL_WIDGET_FINE_PRINT =
  "Up to {{shareUsageLimit}} friends can use your code within {{shareWindow}}. Each time a friend uses your code, we'll email you a one-time reward code.";

export const DEFAULT_VIRAL_WIDGET_SHARE_USAGE_LIMIT = 3;

export const DEFAULT_VIRAL_WIDGET_SHARE_WINDOW_DAYS = 1;

export const DEFAULT_VIRAL_WIDGET_REWARD_WINDOW_DAYS = 30;

export const viralWidgetDiscountValueSchema = z.object({
  valueType: z.enum(ViralWidgetDiscountValueType),
  value: z.number(),
});
export type ViralWidgetDiscountValue = z.infer<
  typeof viralWidgetDiscountValueSchema
>;

export const viralWidgetCombinesWithSchema = z.object({
  orderDiscounts: z.boolean().default(false),
  productDiscounts: z.boolean().default(false),
  shippingDiscounts: z.boolean().default(false),
});
export type ViralWidgetCombinesWith = z.infer<
  typeof viralWidgetCombinesWithSchema
>;

/** The merchant-editable half of the widget; the wire shape of both RPCs. */
export const viralWidgetSettingsSchema = z.object({
  enabled: z.boolean().default(false),
  title: z.string().default(DEFAULT_VIRAL_WIDGET_TITLE),
  message: z.string(),
  finePrint: z.string().default(DEFAULT_VIRAL_WIDGET_FINE_PRINT),
  shareDiscount: viralWidgetDiscountValueSchema,
  rewardDiscount: viralWidgetDiscountValueSchema,
  shareUsageLimit: z.number().default(DEFAULT_VIRAL_WIDGET_SHARE_USAGE_LIMIT),
  shareWindowDays: z.number().default(DEFAULT_VIRAL_WIDGET_SHARE_WINDOW_DAYS),
  rewardWindowDays: z.number().default(DEFAULT_VIRAL_WIDGET_REWARD_WINDOW_DAYS),
  combinesWith: viralWidgetCombinesWithSchema,
});
export interface ViralWidgetSettings extends z.infer<
  typeof viralWidgetSettingsSchema
> {}

export type ViralWidgetCopyValues = Pick<
  ViralWidgetSettings,
  | "shareDiscount"
  | "rewardDiscount"
  | "shareUsageLimit"
  | "shareWindowDays"
  | "rewardWindowDays"
>;

/**
 * Substitutes `{{shareDiscount}}`, `{{rewardDiscount}}`, `{{shareUsageLimit}}`,
 * `{{shareWindow}}`, and `{{rewardWindow}}`; unknown tokens pass through.
 */
export function renderViralWidgetCopy(
  template: string,
  widget: ViralWidgetCopyValues,
  currency: Currency,
): string {
  const values: Record<string, string> = {
    shareDiscount: formatViralDiscountValue(widget.shareDiscount, currency),
    rewardDiscount: formatViralDiscountValue(widget.rewardDiscount, currency),
    shareUsageLimit: String(widget.shareUsageLimit),
    shareWindow: formatDayWindow(widget.shareWindowDays),
    rewardWindow: formatDayWindow(widget.rewardWindowDays),
  };
  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, key: string) =>
    Object.hasOwn(values, key) ? (values[key] ?? match) : match,
  );
}

/** Carries its own unit so merchant copy reads correctly at a one-day window. */
export function formatDayWindow(days: number): string {
  return days === 1 ? "1 day" : `${days} days`;
}

/** Buyer-facing value summary, e.g. "10% off" or "£5 off". */
export function formatViralDiscountValue(
  discount: ViralWidgetDiscountValue,
  currency: Currency,
): string {
  switch (discount.valueType) {
    case ViralWidgetDiscountValueType.Percentage:
      return `${discount.value}% off`;
    case ViralWidgetDiscountValueType.Fixed:
      return `${getCurrencySymbol(currency)}${discount.value} off`;
    default:
      assertNever(discount.valueType);
  }
}
